import React from 'react';
import styles from './ui.module.css';

export interface TabItem {
  value: string;
  label: React.ReactNode;
  /** Optional trailing chip — a count, a status. */
  badge?: React.ReactNode;
}

interface TabBarProps {
  tabs: TabItem[];
  value: string;
  onChange: (value: string) => void;
  /** Names the group for screen readers, e.g. "Notification type". */
  ariaLabel?: string;
  /** Where the bar sits in a flex row: `start` (default) or `end`, to
   *  bottom-align it with labelled controls beside it. */
  align?: 'start' | 'end';
}

/**
 * The app's tab control: a navy segmented pill.
 *
 * One component rather than a copy per page — Orders, Notifications, Risk
 * Calculation and Portfolio Performance each had their own stylesheet for the
 * same thing, and they had already drifted apart.
 */
const TabBar: React.FC<TabBarProps> = ({ tabs, value, onChange, ariaLabel, align = 'start' }) => (
  <div
    className={`${styles.tabBar}${align === 'end' ? ` ${styles.tabBarEnd}` : ''}`}
    role="tablist"
    aria-label={ariaLabel}
  >
    {tabs.map(tab => {
      const active = tab.value === value;
      return (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={active}
          className={`${styles.tabBtn}${active ? ` ${styles.tabBtnActive}` : ''}`}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
          {tab.badge}
        </button>
      );
    })}
  </div>
);

export default TabBar;
